/**
 * Small utility wrapper around localStorage.
 *
 * File: \src\components\general\storage\LocalStorage.ts
 * Created Date: Friday, March 1st 2019, 11:31:15
 * Author: Sascha Herrmann
 * -----
 * Last Modified:
 * Modified By:
 * -----
 * Copyright (c) 2017-2019 Liebherr-Hausgeräte GmbH
 */

/**
 * Main purpose of the class is to be able to handle a fallback when getting an item.
 * So we purely wrap localStorage, but the getItem() method knows the fallback mechanism.
 *
 * @export
 * @class LocalStorage
 */
export default class LocalStorage {
  /**
   * Gets an item from localStorage. If the value does not exist and a fallback is passed,
   * then the fallback is returned. Otherwise it returns null (like localStorage does).
   * The fallback is stored under the given key then.
   *
   * @static
   * @param {string} localStorageKey The key to use for the localStorage access.
   * @param {string} [fallback] The fallback that is returned if the value in localStorage is null.
   *
   * @returns {string} Either the value from localStorage, the fallback if given, or null.
   * @memberof LocalStorage
   */
  public static getItem(localStorageKey: string, fallback?: string): string {
    let value: string = window.localStorage.getItem(localStorageKey);

    if (!value) {
      value = fallback;

      if (!value) {
        window.localStorage.setItem(localStorageKey, value);
      }
    }

    return value;
  }

  /**
   * Sets the item into localStorage with the given key.
   *
   * @static
   * @param {string} localStorageKey The key to use for storing.
   * @param {string} value The value to set.
   * @memberof LocalStorage
   */
  public static setItem(localStorageKey: string, value: string): void {
    window.localStorage.setItem(localStorageKey, value);
  }

  /**
   * Removes an item from localStorage.
   *
   * @static
   * @param {string} localStorageKey The key of the item to remove.
   * @memberof LocalStorage
   */
  public static removeItem(localStorageKey: string): void {
    window.localStorage.removeItem(localStorageKey);
  }
}
